import {Children, FC, ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react'
import clsx from 'clsx'

type Props = {
  children: ReactNode
}

const getVisibleCount = () => {
  if (typeof window === 'undefined') return 4
  if (window.innerWidth >= 992) return 4
  if (window.innerWidth >= 768) return 2
  return 1
}

const GAP_PX = 16

const AttentionIndicatorsCarousel: FC<Props> = ({children}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [visibleCount, setVisibleCount] = useState(() => getVisibleCount())

  const items = Children.toArray(children).filter(Boolean)
  const needsCarousel = items.length > visibleCount

  const updateScrollState = () => {
    const el = trackRef.current
    if (!el) return
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft < maxScroll - 2)
  }

  const applyItemWidths = () => {
    const el = trackRef.current
    if (!el) return

    const nextVisible = getVisibleCount()
    setVisibleCount(nextVisible)

    const trackWidth = el.clientWidth
    if (trackWidth <= 0) return

    const itemWidth = (trackWidth - GAP_PX * (nextVisible - 1)) / nextVisible

    el.querySelectorAll<HTMLElement>('.tr-attention-carousel__item').forEach((item) => {
      item.style.flex = `0 0 ${itemWidth}px`
      item.style.width = `${itemWidth}px`
      item.style.minWidth = `${itemWidth}px`
      item.style.maxWidth = `${itemWidth}px`
    })

    updateScrollState()
  }

  useLayoutEffect(() => {
    applyItemWidths()
  }, [items.length])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    const onScroll = () => updateScrollState()
    el.addEventListener('scroll', onScroll, {passive: true})

    const ro = new ResizeObserver(() => {
      applyItemWidths()
    })
    ro.observe(el)
    window.addEventListener('resize', applyItemWidths)

    const timeout = window.setTimeout(applyItemWidths, 50)

    return () => {
      el.removeEventListener('scroll', onScroll)
      ro.disconnect()
      window.removeEventListener('resize', applyItemWidths)
      window.clearTimeout(timeout)
    }
  }, [items.length])

  const scrollByDirection = (direction: -1 | 1) => {
    const el = trackRef.current
    if (!el) return
    const firstItem = el.querySelector('.tr-attention-carousel__item') as HTMLElement | null
    const amount = firstItem ? firstItem.offsetWidth + GAP_PX : el.clientWidth / visibleCount
    el.scrollBy({left: direction * amount, behavior: 'smooth'})
  }

  if (items.length === 0) {
    return null
  }

  return (
    <div className={clsx('tr-attention-carousel', needsCarousel && 'tr-attention-carousel--scrollable')}>
      {needsCarousel && (
        <button
          type='button'
          className={clsx(
            'tr-attention-carousel__nav tr-attention-carousel__nav--prev',
            !canScrollLeft && 'tr-attention-carousel__nav--disabled'
          )}
          onClick={() => scrollByDirection(-1)}
          disabled={!canScrollLeft}
          aria-label='Indicadores anteriores'
        >
          <i className='bi bi-chevron-left'></i>
        </button>
      )}

      <div className='tr-attention-carousel__track' ref={trackRef}>
        {items.map((child, index) => (
          <div className='tr-attention-carousel__item' key={index}>
            {child}
          </div>
        ))}
      </div>

      {needsCarousel && (
        <button
          type='button'
          className={clsx(
            'tr-attention-carousel__nav tr-attention-carousel__nav--next',
            !canScrollRight && 'tr-attention-carousel__nav--disabled'
          )}
          onClick={() => scrollByDirection(1)}
          disabled={!canScrollRight}
          aria-label='Próximos indicadores'
        >
          <i className='bi bi-chevron-right'></i>
        </button>
      )}
    </div>
  )
}

export {AttentionIndicatorsCarousel}
